import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCategoryAndTitle } from 'utils';
import routeCodes from 'common/constants/route-codes';
import { useDishes } from 'redux/dishes/use-dishes';
import { useCompany } from 'redux/company/use-company';
import {
  useCurrentRestaurant,
  useRestaurant,
} from 'redux/restaurant/use-restaurant';
import AuthService from 'services/Auth';

import {
  setModalData,
  setEditableItem,
  getCategories,
  setCurrentRestaurant,
  getStripeLink,
  resetState as resetStateAction,
  setVisibleLists,
  addVisibleList,
  removeVisibleList,
  resetVisibleLists,
  setHasEnteredMenu,
} from './actions';
import {
  modalDataSelector,
  editableDataSelector,
  categoriesList,
  categoriesLoadingSelector,
  currentRestaurantSelector,
  visibleListsSelector,
  getModalData,
  getCurrentRestaurant,
  getEditableItem,
  getVisibleLists,
  isListVisible,
  getHasEnteredMenu,
  getCategoriesLoading,
} from './selectors';
import UsersApi from 'libs/api/users';
import { useSpecialMenu } from 'redux/special_menu/use-special-menu';
import { useDishOptions } from 'redux/dishes_options/use-dish-options';
import { useDishCategory } from '../dishes_categories/use-dish-category';

const userApi = new UsersApi();

export const useCommon = () => {
  const modalData = useSelector(modalDataSelector);
  const editableItem = useSelector(editableDataSelector);
  const currentRestaurant = useSelector(currentRestaurantSelector);
  const visibleLists = useSelector(visibleListsSelector);
  const { handleDeleteRestaurant } = useRestaurant();
  const { handleCreateOrUpdate } = useCurrentRestaurant();
  const { handleDeleteCompany } = useCompany();
  const { handleDeleteDish } = useDishes();
  const { handleDeleteSpecialMenu } = useSpecialMenu();
  const { handleDelete: handleDeleteDishOption } = useDishOptions();
  const { handleDeleteDishCategory } = useDishCategory();
  const dispatch = useDispatch();

  const handleSetModalData = (payload) => {
    dispatch(setModalData(payload));
  };

  const handleResetModalData = () => {
    dispatch(
      setModalData({
        open: false,
        variant: 'primary',
        title: '',
        body: '',
        itemData: {},
      }),
    );
  };

  const handleGetStripeLink = () => {
    dispatch(getStripeLink());
  };

  const handleSubmitModalAction = () => {
    if (modalData.variant === 'delete') {
      const [category, id] = getCategoryAndTitle(modalData.itemData);
      if (category.includes('restaurants')) {
        handleDeleteRestaurant(id);
      }
      if (category.includes('firms')) {
        handleDeleteCompany(id);
      }
      if (category.includes('dishes')) {
        handleDeleteDish(id);
      }
      if (category.includes('special-menu')) {
        handleDeleteSpecialMenu(id);
      }
      if (category.includes('options')) {
        handleDeleteDishOption(id);
      }
      if (category.includes('team')) {
        // TODO team delete callback
        console.log('delete', category, id);
        (async () => {
          try {
            await userApi.destroy(id);
            handleResetModalData();
          } catch (e) {}
        })();
      }
      if (category.includes('dish-categories')) {
        handleDeleteDishCategory(id);
      }
    }

    if (modalData.variant === 'update') {
      const { id, data, route, restaurantDisabledAction } = modalData.itemData;
      handleCreateOrUpdate(
        data,
        id,
        route !== undefined ? route : routeCodes.home,
        true,
        {
          restaurantDisabledAction:
            restaurantDisabledAction !== undefined
              ? restaurantDisabledAction
              : true,
        },
      );
    }
  };

  const handleSetCurrentRestaurant = (payload) => {
    AuthService.setCurrentRestaurant(payload?.id);
    dispatch(setCurrentRestaurant(payload));
  };

  const handleSetEditableItem = useCallback(
    (payload) => {
      dispatch(setEditableItem(payload));
    },
    [dispatch],
  );

  const resetState = useCallback(() => {
    dispatch(resetStateAction());
  }, [dispatch]);

  // Visible lists management
  const handleSetVisibleLists = useCallback(
    (lists) => {
      dispatch(setVisibleLists(lists));
    },
    [dispatch],
  );

  const handleAddVisibleList = useCallback(
    (listId) => {
      dispatch(addVisibleList(listId));
    },
    [dispatch],
  );

  const handleRemoveVisibleList = useCallback(
    (listId) => {
      dispatch(removeVisibleList(listId));
    },
    [dispatch],
  );

  const handleResetVisibleLists = useCallback(() => {
    dispatch(resetVisibleLists());
  }, [dispatch]);

  const handleDeleteItem = useCallback(
    async (id) => {
      const [category] = getCategoryAndTitle(id);
      if (category === 'dishes') {
        await handleDeleteDish(id);
      } else if (category === 'special-menu') {
        await handleDeleteSpecialMenu(id);
      } else if (category === 'dish-options') {
        await handleDeleteDishOption(id);
      } else if (category === 'dish-categories') {
        await handleDeleteDishCategory(id);
      } else if (category === 'restaurants') {
        await handleDeleteRestaurant(id);
      } else if (category === 'companies') {
        await handleDeleteCompany(id);
      }
    },
    [
      handleDeleteDish,
      handleDeleteSpecialMenu,
      handleDeleteDishOption,
      handleDeleteDishCategory,
      handleDeleteRestaurant,
      handleDeleteCompany,
    ],
  );

  return {
    modalData,
    editableItem,
    handleSetEditableItem,
    handleResetModalData,
    handleSetModalData,
    handleSubmitModalAction,
    handleSetCurrentRestaurant,
    currentRestaurant,
    handleGetStripeLink,
    resetState,
    // Visible lists
    visibleLists,
    handleSetVisibleLists,
    handleAddVisibleList,
    handleRemoveVisibleList,
    handleResetVisibleLists,
    handleDeleteItem,
  };
};

export const useCategories = () => {
  const dispatch = useDispatch();
  const list = useSelector(categoriesList);
  const categoriesLoading = useSelector(categoriesLoadingSelector);

  const handleGetList = (
    payload = {
      params: {
        per: 10000,
      },
    },
  ) => {
    dispatch(getCategories(payload));
  };

  return { list, handleGetList, categoriesLoading };
};

// Modal hooks
export const useModalData = () => {
  const dispatch = useDispatch();
  const modalData = useSelector(getModalData);

  return {
    modalData,
    setModalData: (data) => dispatch(setModalData(data)),
  };
};

// Restaurant hooks
export const useCurrentRestaurantHook = () => {
  const dispatch = useDispatch();
  const currentRestaurant = useSelector(getCurrentRestaurant);

  return {
    currentRestaurant,
    setCurrentRestaurant: (restaurant) =>
      dispatch(setCurrentRestaurant(restaurant)),
  };
};

// Editable item hooks
export const useEditableItem = () => {
  const dispatch = useDispatch();
  const editableItem = useSelector(getEditableItem);

  return {
    editableItem,
    setEditableItem: (item) => dispatch(setEditableItem(item)),
  };
};

// Visible lists hooks
export const useVisibleLists = () => {
  const dispatch = useDispatch();
  const visibleLists = useSelector(getVisibleLists);
  const hasEnteredMenu = useSelector(getHasEnteredMenu);

  return {
    visibleLists,
    hasEnteredMenu,
    setVisibleLists: (lists) => dispatch(setVisibleLists(lists)),
    addVisibleList: (listId) => dispatch(addVisibleList(listId)),
    removeVisibleList: (listId) => dispatch(removeVisibleList(listId)),
    resetVisibleLists: () => dispatch(resetVisibleLists()),
    setHasEnteredMenu: (hasEntered) => dispatch(setHasEnteredMenu(hasEntered)),
  };
};

export const useListVisibility = (listId) => {
  const dispatch = useDispatch();
  const isVisible = useSelector((state) => isListVisible(state, listId));

  return {
    isVisible,
    toggleVisibility: () => {
      if (isVisible) {
        dispatch(removeVisibleList(listId));
      } else {
        dispatch(addVisibleList(listId));
      }
    },
  };
};
